import { useCallback, useRef } from "react";
import { Room } from "livekit-client";
import { Socket } from "socket.io-client";
import { NoiseAnalyzer } from "../utils/noiseAnalysis";
import { forceStopAllMediaStreams } from "../utils/mediaStreamCleanup";

interface UseSessionLifecycleProps {
  isConnecting: boolean;
  isInitializing: boolean;
  setIsInitializing: (initializing: boolean) => void;
  isConnected: boolean;
  audioStreamRef: React.MutableRefObject<MediaStream | null>;
  initializeSocket: () => void;
  getAudioStream: () => Promise<MediaStream | null>;
  setError: (error: string | null) => void;
  setSessionActive: (active: boolean) => void;
  onError?: (error: string) => void;
  stopAudioRecording: (shouldStopTracks?: boolean) => Promise<void>;
  roomRef: React.MutableRefObject<Room | null>;
  socketRef: React.MutableRefObject<Socket | null>;
  setAudioStream: (stream: MediaStream | null) => void;
  silenceTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  cleanupRemoteAudio: () => void;
  chunkQueue: React.MutableRefObject<Int16Array[]>;
  isCurrentlySendingRef: React.MutableRefObject<boolean>;
  noiseAnalyzerRef: React.MutableRefObject<NoiseAnalyzer>;
  noiseCheckDoneRef: React.MutableRefObject<boolean>;
  noiseWarningShownRef: React.MutableRefObject<boolean>;
  isProcessingPausedRef: React.MutableRefObject<boolean>;
  userMutedManuallyRef: React.MutableRefObject<boolean>;
  remoteTracksRef: React.MutableRefObject<Array<{ track: any; kind: string }>>;
  setWarningMessage: (message: string | null) => void;
  setIsWarningError: (error: boolean) => void;
  setIsMicDisabledByWarning: (disabled: boolean) => void;
  reset: () => void;
  onSessionEnd?: () => void;
  prepareDisconnect?: () => void;
  wavRecorderRef?: React.MutableRefObject<any> | null;
}

export const useSessionLifecycle = ({
  isConnecting,
  isInitializing,
  setIsInitializing,
  isConnected,
  initializeSocket,
  getAudioStream,
  setError,
  setSessionActive,
  onError,
  stopAudioRecording,
  roomRef,
  socketRef,
  audioStreamRef,
  setAudioStream,
  silenceTimeoutRef,
  cleanupRemoteAudio,
  chunkQueue,
  isCurrentlySendingRef,
  noiseAnalyzerRef,
  noiseCheckDoneRef,
  noiseWarningShownRef,
  isProcessingPausedRef,
  userMutedManuallyRef,
  remoteTracksRef,
  setWarningMessage,
  setIsWarningError,
  setIsMicDisabledByWarning,
  reset,
  onSessionEnd,
  prepareDisconnect,
  wavRecorderRef,
}: UseSessionLifecycleProps) => {
  const isEndingRef = useRef(false);

  const initializeSession = useCallback(async () => {
    if (isConnecting || isInitializing) return;

    if (isConnected && audioStreamRef.current) {
      const activeTracks = audioStreamRef.current
        .getAudioTracks()
        .filter((track) => track.readyState === "live");
      if (activeTracks.length > 0) {
        return;
      }
    }

    setIsInitializing(true);
    setError(null);

    try {
      if (!isConnected) {
        initializeSocket();
        setSessionActive(true);
      }

      const stream = await getAudioStream();
      if (!stream) {
        setIsInitializing(false);
        return;
      }
    } catch (err: any) {
      const errorMessage = err.message || "Failed to initialize session";
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsInitializing(false);
    }
  }, [
    isConnecting,
    isInitializing,
    isConnected,
    getAudioStream,
    setError,
    setSessionActive,
    onError,
    initializeSocket,
    audioStreamRef,
    setIsInitializing,
  ]);

  const endSession = useCallback(
    async (options?: { close?: boolean }) => {
      if (isEndingRef.current) return;
      isEndingRef.current = true;
      const shouldClose = options?.close !== false;

      try {
        prepareDisconnect?.();

        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }

        // Cleanup AI speaking detection
        cleanupRemoteAudio();

        chunkQueue.current = [];
        isCurrentlySendingRef.current = false;
        noiseCheckDoneRef.current = false;
        noiseWarningShownRef.current = false;
        isProcessingPausedRef.current = false;
        noiseAnalyzerRef.current.reset();
        userMutedManuallyRef.current = false;
        setWarningMessage(null);
        setIsWarningError(false);
        setIsMicDisabledByWarning(false);
        remoteTracksRef.current = [];

        await stopAudioRecording(true);

        if (roomRef.current) {
          await roomRef.current.disconnect().catch(() => {});
          roomRef.current = null;
        }

        forceStopAllMediaStreams(audioStreamRef, wavRecorderRef);

        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }

        reset();
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => resolve()),
        );
        if (shouldClose) {
          onSessionEnd?.();
        }
      } finally {
        isEndingRef.current = false;
      }
    },
    [
      stopAudioRecording,
      reset,
      onSessionEnd,
      prepareDisconnect,
      audioStreamRef,
      silenceTimeoutRef,
      cleanupRemoteAudio,
      chunkQueue,
      isCurrentlySendingRef,
      noiseCheckDoneRef,
      noiseWarningShownRef,
      isProcessingPausedRef,
      noiseAnalyzerRef,
      userMutedManuallyRef,
      remoteTracksRef,
      setWarningMessage,
      setIsWarningError,
      setIsMicDisabledByWarning,
      roomRef,
      socketRef,
      wavRecorderRef,
    ],
  );

  return {
    initializeSession,
    endSession,
  };
};
